import { useState } from 'react';
import { todayISO } from '../domain/clock';
import { useLocale } from '../context/LocaleContext';
import {
  blankTripForm, errorsAfterPatch, repeatTripFields, repeatTripForm, tripCanSave, tripErrors, tripFormForDate,
  tripIsDirty,
} from '../domain/mileage';

// The Add Mileage drawer's state (#476): open or not, a new trip or an edit,
// what it holds, what Save refused, and the Unsaved Changes guard every MMS
// drawer puts in front of a dismissal. The rules — what is refused, what
// counts as a change, what an edit writes — are core/src/mileage.rs, reached
// through src/domain/mileage.js; this is the state around them, kept out of
// the page as the expense drawer's is (`useExpenseEntryForm`).
//
// `unit` is the studio's distance unit, which a new trip is logged in. An
// edit keeps the trip's own, so a Region changed since leaves the distances
// it already holds as they were typed. Its Distance and Odometer open with the
// Region's decimal separator — `12,5` in Germany, as every other number box
// there (#583); the core reads either back.
//
// **An edit is of one row's date (#554).** MMS's Edit opens the drawer at the
// date of the row it was chosen on, and Save of a changed date of a series
// asks "Update Recurring Entries" — "This entry only" or "This and following
// entries" — before writing. `save` asks by setting `scopeQuestion` and
// writing nothing; the page's dialog answers through `answerScope`. A
// one-time trip has no question.
//
// **"Repeat Trip" (#521)** opens Add Mileage with the trip's destination,
// distance, duration and vehicle filled in, as MMS's card button does, and
// marks them "autofilled" until each is edited. Its baseline is that form, so
// opening and closing it asks nothing.
//
// `expense` is Business Settings' Mileage Log tab (#520), as
// `useMileageExpenseSettings` reads it, handed to each save so the provider
// can write the trip's expenses with it.
export default function useMileageForm({ addTrip, updateTrip, unit, expense = null }) {
  const { decimalSeparator } = useLocale();
  const [open, setOpen] = useState(false);
  // `{ trip, date, baseline }` for an edit, null for a new trip.
  const [editing, setEditing] = useState(null);
  // A new trip's baseline when it did not open blank (Repeat Trip).
  const [newBaseline, setNewBaseline] = useState(null);
  const [autofilled, setAutofilled] = useState([]);
  const [form, setForm] = useState(() => blankTripForm(todayISO(), unit));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null); // null | 'save' | 'another'
  const [guard, setGuard] = useState(null); // the dismissal held behind the dialog
  const [scopeQuestion, setScopeQuestion] = useState(false);

  const openNew = () => {
    setEditing(null);
    setNewBaseline(null);
    setAutofilled([]);
    setForm(blankTripForm(todayISO(), unit));
    setErrors({});
    setOpen(true);
  };

  // "Repeat Trip" on a Recent Trips card.
  const openRepeat = (trip) => {
    const filled = repeatTripForm(trip, todayISO(), unit, decimalSeparator);
    setEditing(null);
    setNewBaseline(filled);
    setAutofilled(repeatTripFields());
    setForm(filled);
    setErrors({});
    setOpen(true);
  };

  // `date` is the row's: a date of a series opens there.
  const openEdit = (trip, date) => {
    const baseline = tripFormForDate(trip, date, unit, decimalSeparator);
    setEditing({ trip, date: date || trip.occurredOn || '', baseline, series: trip.recurrence === 'recurring' });
    setNewBaseline(null);
    setAutofilled([]);
    setForm(baseline);
    setErrors({});
    setOpen(true);
  };

  // A field edited clears its own refusal and no other, as the expense
  // drawer's do, and is no longer one Repeat Trip filled in.
  const patch = (fields) => {
    setForm((prev) => ({ ...prev, ...fields }));
    setErrors((prev) => errorsAfterPatch(prev, Object.keys(fields)));
    setAutofilled((prev) => prev.filter((f) => !(f in fields)));
  };

  const closeAll = () => {
    setOpen(false);
    setEditing(null);
    setNewBaseline(null);
    setAutofilled([]);
    setErrors({});
    setGuard(null);
    setScopeQuestion(false);
  };

  const dirty = () => tripIsDirty(form, editing?.baseline ?? newBaseline, todayISO(), unit);

  // Every way out of the drawer — Close, Cancel, Escape, the scrim — asks
  // first when there is something to lose.
  const leave = (next = closeAll) => {
    if (dirty()) { setGuard(() => next); return; }
    next();
  };

  const write = async (andAnother, scope) => {
    setSaving(andAnother ? 'another' : 'save');
    try {
      if (editing) {
        await updateTrip(editing.trip, editing.date, scope, editing.baseline, form, expense);
        closeAll();
        return true;
      }
      await addTrip(form, expense);
      if (andAnother) {
        setForm(blankTripForm(todayISO(), unit));
        setNewBaseline(null);
        setAutofilled([]);
        setErrors({});
      } else {
        closeAll();
      }
      return true;
    } finally {
      setSaving(null);
    }
  };

  // Refused: the drawer stays open with the fields marked, which is what MMS
  // does, and nothing is written. A changed date of a series asks its
  // question first and answers false; the answer saves. Answers whether it
  // saved.
  const save = async (andAnother = false) => {
    const refusals = tripErrors(form);
    setErrors(refusals);
    if (Object.keys(refusals).length) return false;
    if (editing?.series && dirty()) {
      setScopeQuestion(true);
      return false;
    }
    return write(andAnother, 'this');
  };

  // "Update" on "Update Recurring Entries", with the radio chosen.
  const answerScope = async (scope) => {
    setScopeQuestion(false);
    return write(false, scope);
  };

  return {
    open, editing, form, errors, saving, guard, setGuard, autofilled,
    scopeQuestion, cancelScope: () => setScopeQuestion(false), answerScope,
    canSave: tripCanSave(form),
    openNew, openRepeat, openEdit, patch, leave, closeAll, save,
  };
}
